import React from 'react'
import cx from 'classnames'
import * as R from 'ramda'

import { Tabs } from 'components/shared/Tabs/Tabs'
import { TabContent } from './common/TabContent/TabContent'
import { FeatureFlag } from 'components/auth/FeatureFlag'
import { useTabScrollIntoView } from 'hooks'
import { ALIGNMENT_NAMES } from './model'

import styles from './TabsBar.module.scss'

type TabDescriptor = {
  id: string
  label?: React.ReactNode
  sequenceNumber?: number
  isDisabled?: boolean
  isFinished?: boolean
  isInvalid?: boolean
  adminOnly?: boolean
  caution?: number
}

type TabsBarProps = {
  activeTab?: string
  alignment?: string
  className?: string
  tabClassName?: string
  tabs?: TabDescriptor[]
  onClick?: (id: string) => void
  isScrollable?: boolean
  isCentered?: boolean
  isOutlined?: boolean
}

export const TabsBar = ({
  activeTab,
  alignment,
  className,
  tabClassName,
  tabs = [],
  onClick,
  isScrollable,
  isCentered,
  isOutlined,
}: TabsBarProps) => {
  const ref = useTabScrollIntoView(activeTab)

  return (
    <Tabs
      className={cx(styles.tabsBar, styles.alignment, className, {
        [styles['top-left']]: R.equals(alignment, ALIGNMENT_NAMES.TOP_LEFT),
        [styles.centered]: isCentered,
        [styles.outlined]: isOutlined,
      })}
      isScrollable={isScrollable}
    >
      {R.map(
        ({
          id,
          label,
          sequenceNumber,
          isDisabled,
          isFinished,
          isInvalid,
          adminOnly,
          caution,
        }) => {
          const isActiveTab = R.equals(activeTab, id)

          const tabComponent = (
            <Tabs.Tab
              {...(R.not(isDisabled) && { onClick: () => onClick?.(id) })}
              className={cx(styles.tab, tabClassName, {
                [styles.active]: isActiveTab,
                [styles.invalid]: isInvalid,
              })}
              key={id}
              ref={isActiveTab ? ref : null}
              {...{ id, isDisabled }}
            >
              <TabContent
                className={styles.content}
                isActive={isActiveTab}
                {...{
                  label,
                  sequenceNumber,
                  isDisabled,
                  isFinished,
                  isInvalid,
                  caution,
                }}
              />
            </Tabs.Tab>
          )

          if (adminOnly) {
            return (
              <FeatureFlag key={id} accountAdmin>
                {tabComponent}
              </FeatureFlag>
            )
          }

          return tabComponent
        },
        tabs,
      )}
    </Tabs>
  )
}
